import { BigNumber, ethers } from 'ethers'
import { useWeb3React } from '@web3-react/core'
import { useQuery } from 'react-query'
import { useChainId, useIsSupportedChain } from '../network'
import { useAddresses } from '../useAddress'
import { Controller__factory } from '../../typechain'
import { STALE_TIME, ZERO } from '../../constants'
import { Multicall__factory } from '../../typechain/multicall/index'

export type Vault = {
  vaultId: number
  owner: string
  margin: BigNumber
  openPositions: {
    assetId: number
    sqrtPerpPosition: BigNumber
    underlyingAssetOrDebtAmount: BigNumber
    stableAssetOrDebtAmount: BigNumber
    perpPosition: BigNumber
    stableEntryValue: BigNumber
  }[]
}

export function createEmptyPosition(assetId: number) {
  return {
    assetId,
    sqrtPerpPosition: ZERO,
    underlyingAssetOrDebtAmount: ZERO,
    stableAssetOrDebtAmount: ZERO,
    perpPosition: ZERO,
    stableEntryValue: ZERO
  }
}

export function useVault(vaultId: number) {
  const { provider, account } = useWeb3React<ethers.providers.Web3Provider>()
  const supportedChain = useIsSupportedChain()
  const chainId = useChainId()
  const addresses = useAddresses()

  return useQuery<Vault>(
    ['vault', chainId, vaultId],

    async () => {
      if (!account) throw new Error('Account not set')
      if (!provider) throw new Error('provider not set')
      if (!addresses) throw new Error('addresses not set')

      const contract = Controller__factory.connect(
        addresses.Controller,
        provider
      )

      const vault = await contract.getVault(vaultId)

      return {
        vaultId: vault[0].toNumber(),
        owner: vault[1],
        margin: vault[2],
        openPositions: vault[3].map(openPosition => {
          return {
            assetId: openPosition[0].toNumber(),
            sqrtPerpPosition: openPosition[1][1][0],
            underlyingAssetOrDebtAmount: openPosition[1][2][0],
            stableAssetOrDebtAmount: openPosition[1][3][0],
            perpPosition: openPosition[1][0][0],
            stableEntryValue: openPosition[1][0][1].add(openPosition[1][1][1])
          }
        })
      }
    },

    {
      enabled:
        !!account && supportedChain && !!provider && !!addresses && vaultId > 0,
      staleTime: STALE_TIME,
      refetchInterval: 15000
    }
  )
}

export function useVaults(vaultIds: number[]) {
  const { provider, account } = useWeb3React<ethers.providers.Web3Provider>()
  const supportedChain = useIsSupportedChain()
  const chainId = useChainId()
  const addresses = useAddresses()

  return useQuery<Vault[]>(
    ['vaults', chainId, vaultIds],

    async () => {
      if (!account) throw new Error('Account not set')
      if (!provider) throw new Error('provider not set')
      if (!addresses) throw new Error('addresses not set')

      const controller = Controller__factory.connect(
        addresses.Controller,
        provider
      )

      const multicall = Multicall__factory.connect(
        addresses.Multicall2,
        provider
      )

      const calls = vaultIds.map(vaultId => ({
        target: addresses.Controller,
        callData: controller.interface.encodeFunctionData('getVault', [vaultId])
      }))

      const result = await multicall.callStatic.aggregate(calls, {
        from: account
      })

      return result.returnData
        .map(returnData => {
          return controller.interface.decodeFunctionResult(
            'getVault',
            returnData
          )[0]
        })
        .map(decodedResult => {
          return {
            vaultId: decodedResult[0],
            owner: decodedResult[1],
            margin: decodedResult[2],
            openPositions: decodedResult[3].map((openPosition: any) => {
              return {
                assetId: openPosition[0].toNumber(),
                sqrtPerpPosition: openPosition[1][1][0],
                underlyingAssetOrDebtAmount: openPosition[1][2][0],
                stableAssetOrDebtAmount: openPosition[1][3][0],
                perpPosition: openPosition[1][0][0],
                stableEntryValue: openPosition[1][0][1].add(
                  openPosition[1][1][1]
                )
              }
            })
          }
        })
    },

    {
      enabled: !!account && supportedChain && !!provider && !!addresses,
      staleTime: STALE_TIME,
      refetchInterval: 15000
    }
  )
}
